// src/utils/imageCompression.js

/**
 * Client-side image compression.
 *
 * Phone cameras (iPhone especially) produce 4000px+ photos that blow past the
 * 25MB upload limit on the API and nginx. We downscale and re-encode in the
 * browser *before* the file is ever validated or sent, so what leaves the
 * device is already web sized. The original is never uploaded or kept.
 */

export const MAX_WIDTH = 1920;
export const QUALITY = 0.7;

/**
 * MMS preset. Twilio accepts up to 5MB, but carriers drop or badly transcode
 * anything much over ~1MB, and the image is viewed on a phone screen anyway.
 * 1280px at the same 70% quality lands around 150-300KB.
 */
export const MMS_MAX_WIDTH = 1280;
export const MMS_OPTIONS = { maxWidth: MMS_MAX_WIDTH, quality: QUALITY };

// Animated GIFs would lose their animation if redrawn to a canvas.
const SKIP_TYPES = ['image/gif'];

/**
 * Decode a file into something drawable, honoring EXIF orientation so photos
 * taken sideways on a phone don't come out rotated.
 * @param {File} file
 * @returns {Promise<ImageBitmap|HTMLImageElement>}
 */
const decodeImage = async (file) => {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (error) {
      // Older Safari rejects the options bag - fall through to the <img> path.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Could not decode image'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas encoding failed'))),
      type,
      quality
    );
  });

const jpegName = (name) => `${name.replace(/\.[^.]+$/, '') || 'image'}.jpg`;

/**
 * Resize to at most maxWidth and re-encode as JPEG.
 * Never upscales, and falls back to the original file if anything goes wrong
 * so an upload is never lost to a decoding quirk.
 *
 * @param {File} file
 * @param {{ maxWidth?: number, quality?: number }} [options]
 * @returns {Promise<File>} the compressed file, or the original if unchanged
 */
export const compressImage = async (file, options = {}) => {
  const { maxWidth = MAX_WIDTH, quality = QUALITY } = options;

  if (!file || !file.type.startsWith('image/') || SKIP_TYPES.includes(file.type)) {
    return file;
  }

  let source;
  try {
    source = await decodeImage(file);
  } catch (error) {
    console.warn('Image compression skipped, could not decode:', file.name, error);
    return file;
  }

  try {
    const width = source.width;
    const height = source.height;
    if (!width || !height) return file;

    const scale = Math.min(1, maxWidth / width);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);

    const ctx = canvas.getContext('2d');
    // JPEG has no alpha channel - without this, transparent PNGs go black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);

    const blob = await canvasToBlob(canvas, 'image/jpeg', quality);

    // Re-encoding a small PNG or an already-optimized JPEG can make it bigger.
    if (blob.size >= file.size && scale === 1) return file;

    return new File([blob], jpegName(file.name), {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch (error) {
    console.warn('Image compression failed, using original:', file.name, error);
    return file;
  } finally {
    if (typeof source.close === 'function') source.close();
  }
};

export default compressImage;
